import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { transferConversation } from '../api/chatwoot';
import {
  getAgentDisplayInitials,
  parseAgentDisplayLabel
} from '../utils/chatwootPermissions';

const TRANSFER_PANEL_WIDTH_PX = 420;

export default function ChatwootTransferConversationButton({
  conversationId,
  conversationDisplayName = '',
  currentAssigneeId = null,
  agents = [],
  disabled = false,
  onTransferred,
  onNotify
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('pick');
  const [targetId, setTargetId] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);

  const options = useMemo(() => {
    const current = Number(currentAssigneeId);
    return (agents || []).filter((agent) => {
      const id = Number(agent?.id);
      return Number.isFinite(id) && id > 0 && id !== current;
    });
  }, [agents, currentAssigneeId]);

  const filteredOptions = useMemo(() => {
    const term = String(search || '')
      .trim()
      .toLowerCase();
    if (!term) return options;
    return options.filter((agent) => {
      const label = String(agent.displayName || agent.name || '').trim();
      const parsed = parseAgentDisplayLabel(label);
      const haystack = [
        label,
        parsed.username,
        parsed.chatwootName,
        String(agent.crm_username || ''),
        String(agent.name || '')
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [options, search]);

  const selectedAgent = useMemo(() => {
    const destId = Number(targetId);
    if (!Number.isFinite(destId) || destId <= 0) return null;
    return options.find((agent) => Number(agent.id) === destId) || null;
  }, [options, targetId]);

  const selectedLabel = selectedAgent
    ? String(selectedAgent.displayName || selectedAgent.name || '').trim()
    : '';
  const selectedParsed = parseAgentDisplayLabel(selectedLabel);
  const patientName = String(conversationDisplayName || '').trim() || 'este paciente';
  const hasSelection = Boolean(targetId);
  const canContinue = hasSelection && !busy;

  function closePanel() {
    setOpen(false);
    setStep('pick');
    setTargetId('');
    setSearch('');
  }

  useEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return undefined;
    }

    const PANEL_WIDTH = TRANSFER_PANEL_WIDTH_PX;

    function updatePanelPosition() {
      const anchor = wrapRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight || 360;
      let top = rect.top - panelHeight - 10;
      if (top < 12) top = rect.bottom + 10;
      let left = rect.right - PANEL_WIDTH;
      left = Math.max(12, Math.min(left, window.innerWidth - PANEL_WIDTH - 12));
      top = Math.max(12, Math.min(top, window.innerHeight - panelHeight - 12));
      setPanelStyle({
        position: 'fixed',
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${PANEL_WIDTH}px`,
        zIndex: 12050
      });
    }

    updatePanelPosition();
    const raf = requestAnimationFrame(updatePanelPosition);
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, step, filteredOptions.length, search]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event) {
      if (event.key === 'Escape') closePanel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || step !== 'pick' || options.length === 0) return undefined;
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [open, step, options.length]);

  async function executeTransfer() {
    const destId = Number(targetId);
    if (!conversationId || !Number.isFinite(destId) || destId <= 0) {
      onNotify?.('Elegí el operador destino', 'error');
      return;
    }

    const destLabel = selectedLabel || `Agente #${destId}`;
    setBusy(true);
    try {
      const json = await transferConversation(conversationId, {
        assignee_id: destId,
        patient_display_name: patientName
      });
      const data = json?.data || {};
      closePanel();
      onTransferred?.(data);
      onNotify?.(`Conversación transferida a ${destLabel}`, 'ok');
    } catch (err) {
      onNotify?.(err?.message || 'No se pudo transferir la conversación', 'error');
    } finally {
      setBusy(false);
    }
  }

  function handleContinueToConfirm() {
    if (!hasSelection) return;
    setStep('confirm');
  }

  function toggleAgent(id) {
    const idStr = String(id);
    setTargetId((prev) => (prev === idStr ? '' : idStr));
  }

  const noTargets = !options.length;

  const panel = open ? (
    <div
      ref={panelRef}
      className="crm-cw-transfer-panel"
      style={panelStyle || undefined}
      role="dialog"
      aria-label="Transferir conversación"
      aria-modal="true"
    >
      <div className="crm-cw-transfer-panel__head">
        <div className="crm-cw-transfer-panel__head-text">
          <span className="crm-cw-transfer-panel__eyebrow">Atención humana</span>
          <strong>{step === 'confirm' ? 'Confirmar transferencia' : 'Transferir a otro operador'}</strong>
          <span className="crm-cw-transfer-panel__patient">
            Paciente: <em>{patientName}</em>
          </span>
        </div>
        <button
          type="button"
          className="crm-cw-transfer-panel__close"
          onClick={closePanel}
          aria-label="Cerrar"
          disabled={busy}
        >
          ✕
        </button>
      </div>

      {step === 'pick' ? (
        <>
          <div
            className={`crm-cw-transfer-panel__steps${hasSelection ? ' is-ready' : ''}`}
            role="status"
            aria-live="polite"
          >
            <span className={`crm-cw-transfer-panel__step${hasSelection ? ' is-done' : ' is-active'}`}>
              <span className="crm-cw-transfer-panel__step-num">1</span>
              Tocá un operador
            </span>
            <span className="crm-cw-transfer-panel__step-divider" aria-hidden="true" />
            <span
              className={`crm-cw-transfer-panel__step${hasSelection ? ' is-active' : ''}`}
            >
              <span className="crm-cw-transfer-panel__step-num">2</span>
              Continuar
            </span>
          </div>
          <p className="crm-cw-transfer-panel__hint">
            {hasSelection
              ? 'Operador elegido. Tocá la misma fila otra vez para desmarcar, o Continuar para confirmar.'
              : 'Tocá una fila para marcarla; tocá de nuevo la misma fila para desmarcar.'}
          </p>
          {options.length > 0 ? (
            <div className="crm-cw-transfer-panel__search-wrap">
              <label className="crm-cw-transfer-panel__search">
                <span className="crm-cw-transfer-panel__search-icon" aria-hidden="true">
                  ⌕
                </span>
                <input
                  ref={searchInputRef}
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar por usuario o nombre Chatwoot…"
                  autoComplete="off"
                  aria-label="Buscar operador destino"
                />
                {search.trim() ? (
                  <button
                    type="button"
                    className="crm-cw-transfer-panel__search-clear"
                    aria-label="Limpiar búsqueda"
                    onClick={() => {
                      setSearch('');
                      searchInputRef.current?.focus();
                    }}
                  >
                    ✕
                  </button>
                ) : null}
              </label>
              <p className="crm-cw-transfer-panel__search-meta">
                {search.trim()
                  ? `${filteredOptions.length} de ${options.length} operador${
                      options.length === 1 ? '' : 'es'
                    }`
                  : `${options.length} operador${options.length === 1 ? '' : 'es'} disponible${
                      options.length === 1 ? '' : 's'
                    }`}
              </p>
            </div>
          ) : null}
          <ul
            className="crm-cw-transfer-panel__list"
            role="radiogroup"
            aria-label="Operador destino"
          >
            {filteredOptions.length ? (
              filteredOptions.map((agent) => {
                const id = Number(agent.id);
                const label = String(agent.displayName || agent.name || `Agente #${id}`).trim();
                const parsed = parseAgentDisplayLabel(label);
                const selected = Number(targetId) === id;
                const primary = parsed.username || label;
                const secondary = parsed.chatwootName;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`crm-cw-transfer-panel__row${selected ? ' is-selected' : ''}`}
                      onClick={() => toggleAgent(id)}
                    >
                      <span className="crm-cw-transfer-panel__radio-ui" aria-hidden="true" />
                      <span className="crm-cw-transfer-panel__avatar" aria-hidden="true">
                        {getAgentDisplayInitials(label)}
                      </span>
                      <span className="crm-cw-transfer-panel__label">
                        <span className="crm-cw-transfer-panel__name">{primary}</span>
                        {secondary ? (
                          <span className="crm-cw-transfer-panel__meta">Chatwoot · {secondary}</span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="crm-cw-transfer-panel__empty">No hay operadores que coincidan con la búsqueda.</li>
            )}
          </ul>
          <div className="crm-cw-transfer-panel__footer">
            {hasSelection ? (
              <div className="crm-cw-transfer-panel__selection-preview" title={selectedLabel}>
                <span className="crm-cw-transfer-panel__selection-text">
                  Destino: <strong>{selectedParsed.username || selectedLabel}</strong>
                  {selectedParsed.chatwootName ? (
                    <span className="crm-cw-transfer-panel__selection-meta">
                      {' '}
                      · {selectedParsed.chatwootName}
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="crm-cw-transfer-panel__clear-pick"
                  disabled={busy}
                  onClick={() => setTargetId('')}
                >
                  Quitar selección
                </button>
              </div>
            ) : (
              <p className="crm-cw-transfer-panel__selection-empty">
                Ningún operador seleccionado — elegí uno de la lista.
              </p>
            )}
            <div className="crm-cw-transfer-panel__actions">
              <button type="button" className="crm-btn crm-btn-outline" onClick={closePanel} disabled={busy}>
                Cancelar
              </button>
              <button
                type="button"
                className={`crm-btn crm-btn-primary crm-cw-transfer-panel__continue-btn${
                  canContinue ? '' : ' is-locked'
                }`}
                disabled={!canContinue}
                aria-disabled={!canContinue}
                title={canContinue ? 'Ir al paso de confirmación' : 'Seleccioná un operador primero'}
                onClick={handleContinueToConfirm}
              >
                Continuar
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="crm-cw-transfer-panel__steps is-ready" role="status" aria-live="polite">
            <span className="crm-cw-transfer-panel__step is-done">
              <span className="crm-cw-transfer-panel__step-num">1</span>
              Operador
            </span>
            <span className="crm-cw-transfer-panel__step-divider" aria-hidden="true" />
            <span className="crm-cw-transfer-panel__step is-active">
              <span className="crm-cw-transfer-panel__step-num">2</span>
              Confirmar
            </span>
          </div>
          <p className="crm-cw-transfer-panel__hint">
            Revisá el destino. Si está bien, confirmá la transferencia.
          </p>
          <div className="crm-cw-transfer-panel__handoff">
            <div className="crm-cw-transfer-panel__handoff-card">
              <span className="crm-cw-transfer-panel__handoff-tag">Paciente</span>
              <span className="crm-cw-transfer-panel__handoff-name">{patientName}</span>
            </div>
            <span className="crm-cw-transfer-panel__handoff-arrow" aria-hidden="true">
              →
            </span>
            <div className="crm-cw-transfer-panel__handoff-card crm-cw-transfer-panel__handoff-card--operator">
              <span
                className="crm-cw-transfer-panel__avatar crm-cw-transfer-panel__avatar--lg"
                aria-hidden="true"
              >
                {getAgentDisplayInitials(selectedLabel)}
              </span>
              <div className="crm-cw-transfer-panel__label">
                <span className="crm-cw-transfer-panel__handoff-tag">Nuevo operador</span>
                <span className="crm-cw-transfer-panel__handoff-name">
                  {selectedParsed.username || selectedLabel}
                </span>
                {selectedParsed.chatwootName ? (
                  <span className="crm-cw-transfer-panel__meta">
                    Chatwoot · {selectedParsed.chatwootName}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <p className="crm-cw-transfer-panel__warning">
            Se notifica al operador y este chat deja de estar asignado a vos. Anita sigue en pausa.
          </p>
          <div className="crm-cw-transfer-panel__footer">
            <div className="crm-cw-transfer-panel__actions">
              <button
                type="button"
                className="crm-btn crm-btn-outline"
                disabled={busy}
                onClick={() => setStep('pick')}
              >
                Volver
              </button>
              <button
                type="button"
                className="crm-btn crm-btn-primary"
                disabled={busy}
                onClick={() => void executeTransfer()}
              >
                {busy ? 'Transferiendo…' : 'Confirmar transferencia'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="crm-cw-transfer-wrap" ref={wrapRef}>
      <button
        type="button"
        className="crm-cw-human-transfer-btn"
        title={
          noTargets
            ? 'No hay otros operadores con agente Chatwoot habilitado'
            : 'Transferir a otro operador'
        }
        disabled={disabled || busy || noTargets}
        onClick={() => {
          if (noTargets) return;
          if (open) {
            closePanel();
            return;
          }
          setOpen(true);
          setStep('pick');
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {busy ? 'Transferiendo…' : 'Transferir'}
      </button>

      {panel ? createPortal(panel, document.body) : null}
    </div>
  );
}
